/**
 * ATL-1329 — per-product sale and write-off quantities summed over a set of product report docs.
 *
 * `ProductStocksScreen` and `SupplyOrderDetailScreen` each carried this loop inline, and both size a
 * suggested order from it. The docs are the legacy `dailyProductStats` productReport shape,
 * `{ [productKey]: { name, saleQuantity, writeOffQuantity, … }, date, store }`, so a product entry is
 * recognised by having a `name`; `date` and `store` sit beside the products and have none.
 *
 * It lives here so a test can drive the screens' own arithmetic against the docs the read path
 * returns. That coupling is what broke: on Supabase the read returned a different shape, this loop
 * recognised no entry in it, and every total was 0 without an error.
 */

export interface ProductQuantityTotals {
  saleQuantity: number
  writeOffQuantity: number
}

type ProductReportDoc = Record<string, unknown>

const productEntry = (value: unknown): { saleQuantity?: unknown; writeOffQuantity?: unknown } | null => {
  if (value === null || typeof value !== 'object') return null
  const entry = value as { name?: unknown; saleQuantity?: unknown; writeOffQuantity?: unknown }
  return entry.name != null ? entry : null
}

/**
 * A quantity as a number. A numeric string counts (the old inline `a || 0 + b` CONCATENATED one,
 * turning 2 + '3' into '23'); an absent or non-numeric value contributes nothing, as `|| 0` did.
 */
const quantity = (value: unknown): number => {
  const n = typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : NaN
  return Number.isFinite(n) ? n : 0
}

export const sumProductReportQuantities = (
  docs: ReadonlyArray<ProductReportDoc> | null | undefined
): Record<string, ProductQuantityTotals> => {
  const totals: Record<string, ProductQuantityTotals> = {}
  for (const doc of docs ?? []) {
    for (const productKey of Object.keys(doc)) {
      const entry = productEntry(doc[productKey])
      if (!entry) continue
      const running = totals[productKey] ?? { saleQuantity: 0, writeOffQuantity: 0 }
      totals[productKey] = {
        saleQuantity: running.saleQuantity + quantity(entry.saleQuantity),
        writeOffQuantity: running.writeOffQuantity + quantity(entry.writeOffQuantity),
      }
    }
  }
  return totals
}
